import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { Empty, ErrorNote, Loading } from "../components/Feedback";
import { Btn, Card } from "../components/Shared";
import { rememberFocus } from "../focus";
import { AskDialog, SR_ONLY, SuccessNote } from "./admin/parts";

// Account -> Accounts waiting for you (GB, 2026-10-21): people who signed in through Keycloak for
// the first time and named you, a curator, to confirm their account. Shown to accounts with the
// permission `curator` (the only ones a person can name).
//
//   GET  /api/me/confirmations                 [{id, username, full_name, email, institute, created_at,
//                                              missing_profile_fields}]
//   POST /api/me/confirmations/{id}/confirm    submitted -> confirmed (422 names what is missing)
//   POST /api/me/confirmations/{id}/decline    leave it to the administrators
//
// Confirming lets the person into the portal with no project; adding them to a project is a
// separate step on that project's members.

function when(iso) {
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function ConfirmationsCard() {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [declining, setDeclining] = useState(null);
  const headingRef = useRef(null);

  const load = useCallback(() => {
    api.get("/me/confirmations").then(setList).catch(setError);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (u, action) => {
    setError(null); setNotice(null); setDeclining(null);
    const restore = rememberFocus();
    setBusy(u.id);
    try {
      await api.post(`/me/confirmations/${u.id}/${action}`);
      setNotice(action === "confirm"
        ? `${u.full_name || u.username} is confirmed and can use the portal. Add them to a project on its members if they should work in one.`
        : `${u.full_name || u.username} is left to the administrators.`);
      load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
      restore(() => headingRef.current);
    }
  };

  return (
    <Card style={{ marginBottom: 20 }}>
      <h2 id="confirmations" ref={headingRef} tabIndex={-1} style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700 }}>
        Accounts waiting for you
      </h2>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#4b5563" }}>
        People who signed in for the first time and named you to confirm their account. Confirm the
        ones you know; the administrators can confirm them too.
      </p>
      <ErrorNote error={error} />
      <SuccessNote>{notice}</SuccessNote>
      {!list && !error && <Loading />}
      {list && list.length === 0 && <Empty>Nobody is waiting for you.</Empty>}
      {list && list.length > 0 && (
        <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <caption style={{ textAlign: "left", fontSize: 12, color: "#4b5563", paddingBottom: 4 }}>
            {list.length} {list.length === 1 ? "account" : "accounts"} waiting for you
          </caption>
          <thead>
            <tr style={{ textAlign: "left", color: "#4b5563", fontSize: 12 }}>
              <th scope="col" style={{ padding: "6px 8px" }}>Name</th>
              <th scope="col" style={{ padding: "6px 8px" }}>E-mail</th>
              <th scope="col" style={{ padding: "6px 8px" }}>Institute</th>
              <th scope="col" style={{ padding: "6px 8px" }}>Since</th>
              <th scope="col" style={{ padding: "6px 8px" }}><span style={SR_ONLY}>Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {list.map(u => (
              <tr key={u.id} style={{ borderTop: "1px solid #e5e7eb" }}>
                <th scope="row" data-label="Name" style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600 }}>{u.full_name || u.username}</th>
                <td data-label="E-mail" data-break style={{ padding: "6px 8px" }}>{u.email}</td>
                <td data-label="Institute" style={{ padding: "6px 8px" }}>{u.institute || "—"}</td>
                <td data-label="Since" style={{ padding: "6px 8px" }}>{when(u.created_at)}</td>
                <td style={{ padding: "6px 8px" }}>
                  <div data-row-actions style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Btn onClick={() => act(u, "confirm")} disabled={busy === u.id}>
                      Confirm<span style={SR_ONLY}> {u.full_name || u.username}</span>
                    </Btn>
                    <Btn variant="secondary" onClick={() => setDeclining(u)} disabled={busy === u.id}>
                      Leave to the administrators<span style={SR_ONLY}> ({u.full_name || u.username})</span>
                    </Btn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {declining && (
        <AskDialog title={`Leave ${declining.full_name || declining.username} to the administrators?`}
                   confirmLabel="Leave to the administrators" busy={busy === declining.id}
                   onConfirm={() => act(declining, "decline")} onClose={() => setDeclining(null)}>
          <p style={{ margin: 0, fontSize: 14 }}>
            The account keeps waiting; an administrator confirms it or not. It leaves your list.
          </p>
        </AskDialog>
      )}
    </Card>
  );
}
